import React from 'react';
import { Box, GlobalStyles } from '@mui/material';

/**
 * Scroll-reactive, observability-themed backdrop (on-brand blue→green).
 *
 * Scene changes are CROSS-FADED, not switched: every scene's gradient wash is a
 * permanently-stacked layer whose OPACITY transitions (CSS can't interpolate
 * gradient `background`, so transitioning it snapped — that was the sudden
 * "light effect"). Opacity is interpolatable, so scenes now ease gradually.
 *
 * Over the wash floats a large, dense field of telemetry glyphs — line/area
 * charts, bar charts, distributed-trace waterfalls, a live service map with
 * packets flowing the edges, radar sweep, heatmap, SLO donut, histogram,
 * scatter+regression, flame graph, scrolling logs, sparklines and code — all
 * drifting ("antigravity") and drawing themselves in. GPU-friendly
 * (transform/opacity) and disabled under prefers-reduced-motion.
 */
const BLUE = '#3b82f6';
const GREEN = '#22c55e';
const TEAL = '#14b8a6';
const PURPLE = '#a78bfa';
const AMBER = '#f59e0b';

// Per-section scenes: each is a stacked wash layer + grid strength.
export const THEMES = {
  signal:   { wash: 'radial-gradient(1200px 720px at 50% -8%, rgba(37,99,235,0.34), transparent 62%)', grid: 0.12 },
  topology: { wash: 'radial-gradient(1000px 700px at 20% 8%, rgba(59,130,246,0.34), transparent 60%), radial-gradient(900px 640px at 84% 16%, rgba(14,165,233,0.26), transparent 62%)', grid: 0.20 },
  trace:    { wash: 'radial-gradient(1100px 720px at 80% 6%, rgba(20,184,166,0.32), transparent 62%), radial-gradient(800px 600px at 16% 40%, rgba(59,130,246,0.18), transparent 60%)', grid: 0.14 },
  ai:       { wash: 'radial-gradient(1100px 760px at 50% 2%, rgba(16,185,129,0.34), transparent 64%)', grid: 0.11 },
  launch:   { wash: 'radial-gradient(1200px 780px at 40% 0%, rgba(37,99,235,0.30), transparent 58%), radial-gradient(1000px 680px at 66% 28%, rgba(16,185,129,0.26), transparent 62%)', grid: 0.12 },
};

/* ───────────────────────── glyphs (inline SVG) ───────────────────────── */
const LineArea = ({ c1 = BLUE, c2 = GREEN }) => (
  <svg viewBox="0 0 260 130" width="100%" height="100%">
    <defs><linearGradient id="vo-la" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={c1} stopOpacity="0.40" /><stop offset="100%" stopColor={c1} stopOpacity="0" />
    </linearGradient></defs>
    {[28, 56, 84, 112].map((y) => <line key={y} x1="8" x2="252" y1={y} y2={y} stroke="#fff" strokeOpacity="0.05" />)}
    <path d="M8 96 L42 80 L76 88 L110 54 L144 66 L178 34 L212 50 L252 24 L252 122 L8 122 Z" fill="url(#vo-la)" />
    <path className="vo-draw" d="M8 96 L42 80 L76 88 L110 54 L144 66 L178 34 L212 50 L252 24"
      fill="none" stroke={c2} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <circle className="vo-pulse" cx="252" cy="24" r="4.5" fill={c2} />
  </svg>
);

const BarChart = () => {
  const bars = [48, 72, 36, 90, 60, 100, 70, 84, 52, 78];
  return (
    <svg viewBox="0 0 260 130" width="100%" height="100%">
      <line x1="8" x2="252" y1="118" y2="118" stroke="#fff" strokeOpacity="0.12" />
      {bars.map((h, i) => (
        <rect key={i} className="vo-bar" x={12 + i * 25} y={118 - h} width="16" height={h} rx="3"
          fill={i % 3 === 0 ? GREEN : BLUE} fillOpacity="0.82" style={{ transformOrigin: '0 118px', animationDelay: `${i * 0.1}s` }} />
      ))}
    </svg>
  );
};

const TraceWaterfall = () => {
  const spans = [
    ['api-gateway', 8, 244, BLUE], ['auth-svc', 26, 120, GREEN], ['cart-svc', 50, 110, BLUE],
    ['postgres', 70, 70, PURPLE], ['inventory', 96, 84, TEAL], ['payment', 130, 78, GREEN], ['notify', 150, 50, AMBER],
  ];
  return (
    <svg viewBox="0 0 260 150" width="100%" height="100%">
      {spans.map(([t, x, w, c], i) => (
        <rect key={i} className="vo-span" x={x} y={10 + i * 19} width={w} height="11" rx="5.5"
          fill={c} fillOpacity="0.85" style={{ animationDelay: `${i * 0.12}s` }} />
      ))}
    </svg>
  );
};

const ServiceMap = () => (
  <svg viewBox="0 0 260 190" width="100%" height="100%">
    {[['70,46','160,34'],['70,46','44,130'],['160,34','206,118'],['44,130','138,150'],
      ['206,118','138,150'],['160,34','70,46'],['138,150','206,118']].map(([a, b], i) => {
      const [x1, y1] = a.split(','); const [x2, y2] = b.split(',');
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={BLUE} strokeOpacity="0.4" strokeWidth="1.5" />;
    })}
    {[['M70 46 L160 34', BLUE, '0s'], ['M44 130 L138 150', GREEN, '1s'],
      ['M160 34 L206 118', TEAL, '0.5s'], ['M206 118 L138 150', PURPLE, '1.5s']].map(([d, c, delay], i) => (
      <circle key={i} r="3.5" fill={c}>
        <animateMotion dur="2.8s" begin={delay} repeatCount="indefinite" path={d} />
      </circle>
    ))}
    {[[70, 46, BLUE], [160, 34, GREEN], [44, 130, TEAL], [206, 118, BLUE], [138, 150, GREEN]].map(([x, y, c], i) => (
      <g key={i}>
        <circle className="vo-node" cx={x} cy={y} r="12" fill={c} fillOpacity="0.16" style={{ animationDelay: `${i * 0.4}s` }} />
        <circle cx={x} cy={y} r="6.5" fill={c} fillOpacity="0.92" />
      </g>
    ))}
  </svg>
);

const Radar = () => (
  <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
    <svg viewBox="0 0 140 140" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      {[22, 44, 66].map((r) => <circle key={r} cx="70" cy="70" r={r} fill="none" stroke={GREEN} strokeOpacity="0.2" />)}
      <line x1="6" y1="70" x2="134" y2="70" stroke={GREEN} strokeOpacity="0.14" />
      <line x1="70" y1="6" x2="70" y2="134" stroke={GREEN} strokeOpacity="0.14" />
      {[[92, 52], [50, 46], [104, 92], [46, 96], [80, 110]].map(([x, y], i) => (
        <circle key={i} className="vo-blip" cx={x} cy={y} r="3.5" fill={GREEN} style={{ animationDelay: `${i * 0.6}s` }} />
      ))}
    </svg>
    <Box className="vo-spin" sx={{ position: 'absolute', inset: 0, borderRadius: '50%',
      background: `conic-gradient(from 0deg, transparent 0deg, ${GREEN}40 44deg, transparent 86deg)` }} />
  </Box>
);

const Heatmap = () => {
  const cols = 14, rowsN = 7;
  return (
    <svg viewBox="0 0 260 140" width="100%" height="100%">
      {Array.from({ length: cols * rowsN }).map((_, i) => {
        const x = (i % cols) * 18 + 6, y = Math.floor(i / cols) * 18 + 6;
        const c = (i * 53) % 3 === 0 ? GREEN : (i * 17) % 4 === 0 ? AMBER : BLUE;
        return <rect key={i} className="vo-twinkle" x={x} y={y} width="15" height="15" rx="2.5" fill={c}
          style={{ animationDelay: `${((i * 131) % 240) / 100}s` }} />;
      })}
    </svg>
  );
};

const Donut = ({ c2 = GREEN }) => (
  <svg viewBox="0 0 120 120" width="100%" height="100%">
    <circle cx="60" cy="60" r="44" fill="none" stroke="#fff" strokeOpacity="0.10" strokeWidth="12" />
    <circle className="vo-ring" cx="60" cy="60" r="44" fill="none" stroke={c2} strokeWidth="12" strokeLinecap="round"
      transform="rotate(-90 60 60)" strokeDasharray="276" />
    <circle cx="60" cy="60" r="26" fill={c2} fillOpacity="0.06" />
  </svg>
);

const Histogram = () => {
  const bars = [12, 26, 48, 78, 100, 86, 58, 34, 18, 10];
  return (
    <svg viewBox="0 0 240 130" width="100%" height="100%">
      <line x1="8" x2="232" y1="118" y2="118" stroke="#fff" strokeOpacity="0.12" />
      {bars.map((h, i) => (
        <rect key={i} className="vo-bar" x={12 + i * 22} y={118 - h} width="18" height={h} rx="2"
          fill={TEAL} fillOpacity={0.35 + (h / 100) * 0.5} style={{ transformOrigin: '0 118px', animationDelay: `${i * 0.08}s` }} />
      ))}
    </svg>
  );
};

const Scatter = () => {
  const pts = [[24, 96], [50, 84], [44, 100], [78, 78], [96, 66], [110, 74], [134, 52], [150, 60], [176, 40], [196, 48], [220, 30]];
  return (
    <svg viewBox="0 0 240 120" width="100%" height="100%">
      <path className="vo-draw" d="M16 102 L232 28" stroke={BLUE} strokeOpacity="0.5" strokeWidth="2" strokeDasharray="6 6" fill="none" />
      {pts.map(([x, y], i) => (
        <circle key={i} className="vo-fade" cx={x} cy={y} r="4" fill={i % 2 ? GREEN : BLUE} style={{ animationDelay: `${i * 0.12}s` }} />
      ))}
    </svg>
  );
};

const FlameGraph = () => {
  const rows = [
    [[8, 244, BLUE]],
    [[8, 150, GREEN], [162, 90, TEAL]],
    [[8, 80, PURPLE], [92, 58, BLUE], [162, 90, GREEN]],
    [[8, 50, AMBER], [62, 26, GREEN], [162, 48, BLUE], [214, 38, TEAL]],
  ];
  return (
    <svg viewBox="0 0 260 120" width="100%" height="100%">
      {rows.map((row, r) => row.map(([x, w, c], i) => (
        <rect key={`${r}-${i}`} className="vo-span" x={x} y={8 + r * 26} width={w} height="22" rx="3"
          fill={c} fillOpacity="0.8" style={{ animationDelay: `${(r * 2 + i) * 0.1}s` }} />
      )))}
    </svg>
  );
};

const LogStream = () => {
  const rows = [GREEN, BLUE, '#9aa6b8', GREEN, AMBER, BLUE, '#9aa6b8', GREEN, TEAL, BLUE];
  return (
    <svg viewBox="0 0 240 150" width="100%" height="100%">
      <g className="vo-logscroll">
        {rows.concat(rows).map((c, i) => (
          <g key={i}>
            <circle cx="9" cy={11 + i * 15} r="3" fill={c} />
            <rect x="18" y={6 + i * 15} width={48 + ((i * 37) % 150)} height="9" rx="2" fill={c} fillOpacity="0.45" />
          </g>
        ))}
      </g>
    </svg>
  );
};

const Spark = ({ c2 = GREEN }) => (
  <svg viewBox="0 0 150 50" width="100%" height="100%">
    <path className="vo-draw" d="M6 38 L26 28 L46 32 L66 16 L86 22 L106 10 L126 18 L144 8"
      fill="none" stroke={c2} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CodeBlock = () => {
  const rows = [[12, GREEN], [30, BLUE], [20, '#9aa6b8'], [34, GREEN], [16, BLUE], [26, '#9aa6b8'], [22, GREEN]];
  return (
    <svg viewBox="0 0 240 140" width="100%" height="100%">
      {rows.map(([w, col], i) => (
        <g key={i}>
          <rect x="8" y={12 + i * 18} width="5" height="10" rx="2" fill="#6b7689" fillOpacity="0.5" />
          <rect x="20" y={12 + i * 18} width={w * 6} height="10" rx="3" fill={col} fillOpacity="0.7" />
        </g>
      ))}
      <rect className="vo-scan" x="8" y="8" width="224" height="16" rx="3" fill="#fff" fillOpacity="0.06" />
    </svg>
  );
};

const GLYPHS = { LineArea, BarChart, TraceWaterfall, ServiceMap, Radar, Heatmap, Donut, Histogram, Scatter, FlameGraph, LogStream, Spark, CodeBlock };

// Dense, large field. {type, top, left, width(px), float dur(s), delay(s), opacity}
const FIELD = [
  { type: 'ServiceMap',     top: '5%',  left: '2%',  w: 360, dur: 30, delay: 0,   op: 0.26 },
  { type: 'TraceWaterfall', top: '13%', left: '70%', w: 360, dur: 34, delay: -7,  op: 0.24 },
  { type: 'LineArea',       top: '52%', left: '1%',  w: 340, dur: 30, delay: -4,  op: 0.24 },
  { type: 'FlameGraph',     top: '64%', left: '70%', w: 350, dur: 33, delay: -10, op: 0.20, sm: false },
  { type: 'Heatmap',        top: '34%', left: '78%', w: 300, dur: 36, delay: -3,  op: 0.18, sm: false },
  { type: 'BarChart',       top: '80%', left: '6%',  w: 300, dur: 31, delay: -6,  op: 0.20 },
  { type: 'LogStream',      top: '40%', left: '2%',  w: 250, dur: 28, delay: -9,  op: 0.18, sm: false },
  { type: 'Radar',          top: '8%',  left: '46%', w: 200, dur: 27, delay: -2,  op: 0.22 },
  { type: 'Scatter',        top: '70%', left: '40%', w: 300, dur: 29, delay: -5,  op: 0.16, sm: false },
  { type: 'Histogram',      top: '50%', left: '44%', w: 280, dur: 32, delay: -12, op: 0.15, sm: false },
  { type: 'Donut',          top: '24%', left: '30%', w: 150, dur: 24, delay: -1,  op: 0.20, sm: false },
  { type: 'CodeBlock',      top: '85%', left: '74%', w: 280, dur: 35, delay: -8,  op: 0.16, sm: false },
  { type: 'Spark',          top: '30%', left: '60%', w: 190, dur: 23, delay: -6,  op: 0.16, sm: false },
  { type: 'Donut',          top: '88%', left: '52%', w: 120, dur: 26, delay: -3,  op: 0.16, sm: false },
];

const AuroraBackground = ({ theme = 'signal' }) => {
  const active = THEMES[theme] || THEMES.signal;
  return (
    <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <GlobalStyles styles={{
        '@keyframes vo-float': { '0%,100%': { transform: 'translate3d(0,0,0)' }, '50%': { transform: 'translate3d(0,-30px,0)' } },
        '@keyframes vo-floatx': { '0%,100%': { transform: 'translate3d(0,0,0)' }, '50%': { transform: 'translate3d(22px,-18px,0)' } },
        '@keyframes vo-draw': { '0%': { strokeDashoffset: 420 }, '55%,100%': { strokeDashoffset: 0 } },
        '@keyframes vo-bar': { '0%': { transform: 'scaleY(0)' }, '60%,100%': { transform: 'scaleY(1)' } },
        '@keyframes vo-span': { '0%': { transform: 'translateX(-14px)', opacity: 0 }, '60%,100%': { transform: 'none', opacity: 1 } },
        '@keyframes vo-spin': { '0%': { transform: 'rotate(0)' }, '100%': { transform: 'rotate(360deg)' } },
        '@keyframes vo-twinkle': { '0%,100%': { opacity: 0.12 }, '50%': { opacity: 0.72 } },
        '@keyframes vo-blip': { '0%,100%': { opacity: 0 }, '20%': { opacity: 1 }, '60%': { opacity: 0.2 } },
        '@keyframes vo-logscroll': { '0%': { transform: 'translateY(0)' }, '100%': { transform: 'translateY(-150px)' } },
        '@keyframes vo-node': { '0%,100%': { transform: 'scale(1)', opacity: 0.16 }, '50%': { transform: 'scale(1.5)', opacity: 0.4 } },
        '@keyframes vo-ring': { '0%': { strokeDashoffset: 276 }, '60%,100%': { strokeDashoffset: 70 } },
        '@keyframes vo-fade': { '0%': { opacity: 0, transform: 'scale(0.4)' }, '60%,100%': { opacity: 1, transform: 'scale(1)' } },
        '@keyframes vo-scan': { '0%': { transform: 'translateY(0)', opacity: 0.5 }, '100%': { transform: 'translateY(110px)', opacity: 0 } },
        '@keyframes vo-pulse': { '0%,100%': { r: 4, opacity: 1 }, '50%': { r: 6.5, opacity: 0.5 } },
        '.vo-draw': { strokeDasharray: 420, animation: 'vo-draw 7s ease-in-out infinite' },
        '.vo-bar': { animation: 'vo-bar 6s ease-in-out infinite' },
        '.vo-span': { animation: 'vo-span 6s ease-in-out infinite' },
        '.vo-spin': { animation: 'vo-spin 6s linear infinite', transformOrigin: '50% 50%' },
        '.vo-twinkle': { animation: 'vo-twinkle 3.6s ease-in-out infinite' },
        '.vo-blip': { animation: 'vo-blip 3s ease-in-out infinite' },
        '.vo-logscroll': { animation: 'vo-logscroll 10s linear infinite' },
        '.vo-node': { animation: 'vo-node 3s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' },
        '.vo-ring': { strokeDasharray: 276, animation: 'vo-ring 7s ease-in-out infinite' },
        '.vo-fade': { animation: 'vo-fade 6s ease-in-out infinite', transformBox: 'fill-box', transformOrigin: 'center' },
        '.vo-scan': { animation: 'vo-scan 5s ease-in-out infinite' },
        '.vo-pulse': { animation: 'vo-pulse 2.4s ease-in-out infinite' },
        '@media (prefers-reduced-motion: reduce)': {
          '.vo-glyph': { animation: 'none !important' },
          '[class^="vo-"]': { animation: 'none !important', strokeDashoffset: '0 !important' },
        },
      }} />
      {/* base */}
      <Box sx={{ position: 'absolute', inset: 0, background:
        'radial-gradient(1300px 700px at 50% -8%, #11233f 0%, #0c111a 52%, #0a0d12 100%)' }} />
      {/* scene washes — all stacked, cross-faded by OPACITY (gradual, not sudden) */}
      {Object.entries(THEMES).map(([key, t]) => (
        <Box key={key} sx={{ position: 'absolute', inset: 0, background: t.wash,
          opacity: key === theme ? 1 : 0, transition: 'opacity 1500ms ease-in-out' }} />
      ))}
      {/* dashboard grid — strength eases per scene */}
      <Box sx={{ position: 'absolute', inset: 0, opacity: active.grid, transition: 'opacity 1500ms ease-in-out',
        backgroundImage: 'linear-gradient(rgba(59,130,246,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,0.5) 1px, transparent 1px)',
        backgroundSize: '52px 52px', maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 80%)' }} />
      {/* big floating telemetry glyph field */}
      {FIELD.map((g, i) => {
        const G = GLYPHS[g.type];
        return (
          <Box key={i} className="vo-glyph" sx={{ position: 'absolute', top: g.top, left: g.left, width: g.w, opacity: g.op,
            animation: `${i % 3 === 0 ? 'vo-floatx' : 'vo-float'} ${g.dur}s ease-in-out ${g.delay}s infinite`,
            display: { xs: g.sm === false ? 'none' : 'block', md: 'block' },
            filter: 'drop-shadow(0 8px 26px rgba(0,0,0,0.45))' }}>
            <G />
          </Box>
        );
      })}
      {/* vignette keeps foreground text legible over the denser field */}
      <Box sx={{ position: 'absolute', inset: 0, background:
        'radial-gradient(125% 80% at 50% 34%, transparent 34%, rgba(10,13,18,0.78) 100%)' }} />
    </Box>
  );
};

export default AuroraBackground;
